import { useMemo } from 'preact/hooks';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { loadable } from 'jotai/utils';

import { useAllParams } from './parameterStore';
import { multiParamToArray } from './parameterManagement';
import config from './config';

const initializationState = atom(config.launcherGenerator.getInitializationPromise());
const initializationStateLoadable = loadable(initializationState);

const generationState = atom({generating: false, error: false});

const GENERATION_TYPE_ZIP = "zip";
const GENERATION_TYPE_COMMANDS = "commands";
const GENERATION_TYPE_PREVIEW = "preview";
const GENERATION_TYPE_DIFF = "diff";

const stripWebImageExceptionNoise = (message) => {
    if (!message) {
        return message;
    }

    return message
        .replace(/^Could not generate application\. Reason:\s*"/, "")
        .replace(/"$/, "")
        .replace(/(?:java\.lang\.)?(?:ExceptionInInitializerError|RuntimeException)\s+at\s+cloud\.graal\.gdk\.ui\.API\.main\(:0\)\s+Caused by:\s+/g, "")
        .replace(/(?:Caused by:\s+)?java\.lang\.IllegalArgumentException:\s*/g, "")
        .replace(/\s+at\s+cloud\.graal\.gdk\.ui\.API\.main\(:0\)\s*/g, "")
        .trim();
};

const hideInternalFeatureDetails = (message) => {
    if (!message?.startsWith("The selected features are incompatible.")) {
        return message;
    }

    return message.replace(/\[([^[\]]+)\]/, (match, rawFeatures) => {
        const visibleFeatures = rawFeatures
            .split(",")
            .map(feature => feature.trim())
            .filter(feature => feature && feature !== "kotlin-extension-functions");

        return visibleFeatures.length > 0 ? `[${visibleFeatures.join(", ")}]` : match;
    });
};

const mapGenerationErrorMessage = (error) => {
    let message = stripWebImageExceptionNoise(error?.message || error?.toString());
    if (message === "GraalVM is not supported in Groovy applications") {
        return "Groovy applications are incompatible with GraalVM Native Image because of their reliance on reflection";
    }
    if (message?.includes("experimental-wasm-custom-descriptors") ||
        message?.includes("invalid heap type 'exact'")) {
        return "The current browser cannot run this GDK WebAssembly launcher build. Try a browser/runtime with the required experimental WebAssembly support enabled, or use a compatible browser build for validation.";
    }
    return hideInternalFeatureDetails(message);
};

const useGenerate = () => {
    const parameters = useAllParams();
    const [generationStateValue, setGenerationState] = useAtom(generationState);

    return async (generateType) => {
        if (generationStateValue.generating) {
            return;
        }
        setGenerationState({generating: true, error: false});

        const features = parameters.availableFeatures
            .map(f => f.name)
            .filter(name => parameters.features[name]);

        const parametersObj = {
            ...parameters,
            clouds: multiParamToArray(parameters.clouds),
            services: multiParamToArray(parameters.services),
            features: features,
            availableFeatures: undefined
        }

        try {
            switch(generateType) {
                case GENERATION_TYPE_ZIP:
                    await config.launcherGenerator.generateZip(parametersObj);
                    setGenerationState({generating: false, error: false});
                    break;
                case GENERATION_TYPE_COMMANDS:
                    const commandContents = await config.launcherGenerator.generateCommands(parametersObj);
                    setGenerationState({generating: false, error: false, contents: commandContents, isCommand: true });
                    break;
                case GENERATION_TYPE_PREVIEW:
                    const contents = await config.launcherGenerator.generatePreview(parametersObj);
                    setGenerationState({generating: false, error: false, contents: contents });
                    break;
                case GENERATION_TYPE_DIFF:
                    const diffContents = await config.launcherGenerator.generateDiff(parametersObj);
                    setGenerationState({generating: false, error: false, contents: diffContents, isDiff: true });
                    break;
                default:
                    const error = new Error("Unknown generation type '" + generateType + "'");
                    setGenerationState({generating: false, error});
            }
        } catch (error) {
            setGenerationState({generating: false, error: error})
        }
    }
};

const useGenerationLoading = () => {
    const generationStateValue = useAtomValue(generationState);
    const initializationLoadable = useAtomValue(initializationStateLoadable);
    return generationStateValue.generating || initializationLoadable.state === "loading";
};

const useGenerationDisabled = () => {
    const generationStateValue = useAtomValue(generationState);
    const initializationLoadable = useAtomValue(initializationStateLoadable);
    return generationStateValue.generating ||
        initializationLoadable.state === "loading" ||
        initializationLoadable.state === "hasError";
};


const useGenerationContents = () => {
    const generationStateValue = useAtomValue(generationState);
    return generationStateValue.contents;
}

const useGenerationDiff = () => {
    const generationStateValue = useAtomValue(generationState);
    return generationStateValue.isDiff;
}

const useGenerationCommand = () => {
    const generationStateValue = useAtomValue(generationState);
    return generationStateValue.isCommand;
}

const useClearGeneration = () => {
    const setValue = useSetAtom(generationState);
    return () => setValue( {generating: false, error: false});
}

const useGenerationError = () => {
    const generationStateValue = useAtomValue(generationState);
    const initializationLoadable = useAtomValue(initializationStateLoadable);
    return useMemo(() => {
        const error = generationStateValue?.error ||
            (initializationLoadable.state === "hasError" ? initializationLoadable.error : null);
        if (error) {
            const message = mapGenerationErrorMessage(error);
            return new Error(`Could not generate application. Reason: "${message}"`);
        }
        return null;
    }, [generationStateValue, initializationLoadable])
};

export {
    useGenerate,
    useGenerationLoading,
    useGenerationDisabled,
    useGenerationError,
    useGenerationContents,
    useGenerationDiff,
    useGenerationCommand,
    useClearGeneration,
    GENERATION_TYPE_ZIP,
    GENERATION_TYPE_COMMANDS,
    GENERATION_TYPE_PREVIEW,
    GENERATION_TYPE_DIFF
};
